import type { TSocial } from '@chemist4u/newt-components';

type BrandConfig = {
  /** Display name, e.g. "MyPharmacy" */
  name: string;
  /** Domain shown in titles, e.g. "MyPharmacy.co.uk" */
  domain: string;
  /** Path to logo SVG in /public */
  logo: string;
  /** Compact logo shown when the minimal nav is scrolled (defaults to logo) */
  scrolledLogo?: string;
  /** Short tagline used in meta description fallback */
  tagline: string;
  heroText: string;
  heroSubText: string;

  // SEO
  /** Canonical origin URL, e.g. "https://www.mypharmacy.co.uk" */
  url: string;
  /** Default OG image path (relative to public/) */
  ogImage: string;
  /** Twitter/X handle including @, or null if none */
  twitterHandle: string | null;
  /** Social/professional profile URLs for schema.org sameAs */
  sameAs?: string[];
  /** Centralized social media links (Facebook, Instagram, LinkedIn, X) */
  socialLinks: TSocial[];
  /** Primary customer-facing telephone number */
  telephone?: string;
  /** Primary customer-facing email address */
  email?: string;

  // Icons / PWA / theming
  /** Base path for brand-specific favicons and PWA icons (relative to public/) */
  iconBasePath: string;
  /** Primary theme colour (used in manifest & meta theme-color) */
  themeColor: string;
  /** Background colour for splash screen */
  backgroundColor: string;

  // Regulatory / legal
  legalEntity: string;
  address: string;
  /** Structured postal address for JSON-LD schemas */
  structuredAddress: {
    streetAddress: string;
    addressLocality: string;
    addressRegion: string;
    postalCode: string;
    addressCountry: string;
  };
  vatReg: string;
  companyNo: string;
  gphcReg: string;
  /**
   * Copyright template. Supported placeholders:
   * {year}, {name}, {legalEntity}, {address}, {gphcReg}, {companyNo}, {vatReg}
   */
  copyrightTemplate: string;
};

/** Resolve all placeholders in the copyright template. */
export function getCopyright(b: BrandConfig): string {
  return b.copyrightTemplate
    .replace('{year}', new Date().getFullYear().toString())
    .replace('{name}', b.name)
    .replace('{legalEntity}', b.legalEntity)
    .replace('{address}', b.address)
    .replace('{gphcReg}', b.gphcReg)
    .replace('{companyNo}', b.companyNo)
    .replace('{vatReg}', b.vatReg);
}

const brands: Record<string, BrandConfig> = {
  myp: {
    name: 'MyPharmacy',
    domain: 'MyPharmacy.co.uk',
    logo: '/brands/myp/myPharmacy-logo.svg',
    scrolledLogo: '/brands/myp/web-app-manifest-192x192.png',
    tagline:
      'Your trusted online pharmacy. With treatments for over 50 conditions delivered discreetly to your door.',
    heroText: 'Your trusted online pharmacy',
    heroSubText: 'With treatments for over 50 conditions',

    url: 'https://www.mypharmacy.co.uk',
    ogImage: '/og-image.jpg?v=2',
    twitterHandle: null,
    // Verifiable identity links — primarily for schema.org `sameAs` to
    // help AI search engines and Google's Knowledge Panel link the site
    // to its external entity graph. The GPhC public register URL is
    // computed from `gphcReg` below; add Trustpilot, social profiles,
    // and Companies House when those URLs are confirmed.
    sameAs: [
      'https://www.pharmacyregulation.org/registers/pharmacy/9012464',
      'https://uk.trustpilot.com/review/www.mypharmacy.co.uk',
      'https://www.facebook.com/MyPharmacyUK/',
      'https://uk.linkedin.com/company/my-pharmacy-uk',
      'https://www.instagram.com/mypharmacyuk/',
      'https://find-and-update.company-information.service.gov.uk/company/07262043',
    ],
    socialLinks: [
      {
        name: 'Facebook',
        href: 'https://www.facebook.com/MyPharmacyUK/',
        icon: 'facebook',
        platform: 'facebook',
      },
      {
        name: 'Instagram',
        href: 'https://www.instagram.com/mypharmacyuk/',
        icon: 'instagram',
        platform: 'instagram',
      },
      {
        name: 'LinkedIn',
        href: 'https://uk.linkedin.com/company/my-pharmacy-uk',
        icon: 'linkedin',
        platform: 'linkedin',
      },
      {
        name: 'X',
        href: 'https://twitter.com/MyPharmacyGB',
        icon: 'x',
        platform: 'x',
      },
    ],
    telephone: '+441695662153',
    email: 'info@mypharmacy.co.uk',
    iconBasePath: '/brands/myp',
    themeColor: '#17f0de',
    backgroundColor: '#ffffff',

    legalEntity: 'Innox Trading Ltd',
    address: '1 Penketh Place, Skelmersdale, Lancashire, WN8 9QX, GB',
    structuredAddress: {
      streetAddress: '1 Penketh Place',
      addressLocality: 'Skelmersdale',
      addressRegion: 'Lancashire',
      postalCode: 'WN8 9QX',
      addressCountry: 'GB',
    },
    vatReg: 'GB140138454',
    companyNo: '07262043',
    gphcReg: '9012464',
    copyrightTemplate:
      '\u00A9 {year} {name}. {legalEntity}, {address}. All rights reserved. Registered in England No. {companyNo} | VAT Registration No. {vatReg}',
  },
  c4u: {
    name: 'Chemist4U',
    domain: 'Chemist4U.co.uk',
    logo: '/brands/c4u/chemist4u-logo.svg',
    tagline: 'Find the right treatment & feel better, sooner.',
    heroText: 'Find the right treatment & feel better, sooner',
    heroSubText: 'Expert support for a healthier, happier you',

    url: 'https://www.chemist4u.co.uk',
    ogImage: '/og-image.jpg?v=2',
    twitterHandle: null,
    // Verifiable identity links — primarily for schema.org `sameAs` to
    // help AI search engines and Google's Knowledge Panel link the site
    // to its external entity graph. The GPhC public register URL is
    // computed from `gphcReg` below; add Trustpilot, social profiles,
    // and Companies House when those URLs are confirmed.
    sameAs: [
      'https://www.pharmacyregulation.org/registers/pharmacy/9012464',
      'https://uk.trustpilot.com/review/www.chemist-4-u.com',
      'https://www.facebook.com/chemist4u/',
      'https://www.linkedin.com/company/innoxtrading/',
      'https://www.instagram.com/chemist4u/',
      'https://find-and-update.company-information.service.gov.uk/company/07262043',
    ],
    socialLinks: [
      {
        name: 'Facebook',
        href: 'https://www.facebook.com/chemist4u/',
        icon: 'facebook',
        platform: 'facebook',
      },
      {
        name: 'Instagram',
        href: 'https://www.instagram.com/chemist4u/',
        icon: 'instagram',
        platform: 'instagram',
      },
      {
        name: 'LinkedIn',
        href: 'https://www.linkedin.com/company/innoxtrading/',
        icon: 'linkedin',
        platform: 'linkedin',
      },
      {
        name: 'X',
        href: 'https://twitter.com/Chemist4U',
        icon: 'x',
        platform: 'x',
      },
    ],
    telephone: '+441695474433',
    iconBasePath: '/brands/c4u',
    themeColor: '#2bb673',
    backgroundColor: '#ffffff',

    legalEntity: 'Innox Trading Ltd',
    address: '1 Penketh Place, Skelmersdale, Lancashire, WN8 9QX, GB',
    structuredAddress: {
      streetAddress: '1 Penketh Place',
      addressLocality: 'Skelmersdale',
      addressRegion: 'Lancashire',
      postalCode: 'WN8 9QX',
      addressCountry: 'GB',
    },
    vatReg: 'GB140138454',
    companyNo: '07262043',
    gphcReg: '9012464',
    copyrightTemplate:
      '\u00A9 {year} {name}. {legalEntity}, {address}. All rights reserved. Registered in England No. {companyNo} | VAT Registration No. {vatReg}',
  },
};

// Canonical brand key aliases — allows both short ('myp') and full ('mypharmacy')
// names to resolve to the same config.
brands.mypharmacy = brands.myp;

// Placeholder brand configs for new token-driven brands.
// TODO: Fill in real values (domain, legal, etc.) when these sites launch.
brands.mybmi = {
  ...brands.myp,
  name: 'MyBMI',
  domain: 'MyBMI.co.uk',
  logo: '/mybmi-logo.svg',
  tagline: 'Expert weight management support.',
  heroText: 'Expert weight management support',
  heroSubText: 'Personalised plans for lasting results',
  url: 'https://www.mybmi.co.uk',
  iconBasePath: '/brands/mybmi',
  themeColor: '#402e3b',
  telephone: '+442046238825',
  email: 'support@my-bmi.co.uk',
  // TODO: Update socialLinks with real MyBMI social accounts when available
};
brands.bmi = brands.mybmi;

brands.myweightloss = {
  ...brands.myp,
  name: 'MyWeightLoss',
  domain: 'MyWeightLoss.co.uk',
  logo: '/myweightloss-logo.svg',
  tagline: 'Your weight loss journey starts here.',
  heroText: 'Your weight loss journey starts here',
  heroSubText: 'Clinically proven treatments, delivered to your door',
  url: 'https://www.myweightloss.co.uk',
  iconBasePath: '/brands/myweightloss',
  themeColor: '#002535',
  // TODO: Update socialLinks with real MyWeightLoss social accounts when available
};
brands.mwl = brands.myweightloss;
// c4u uses its own brand config (defined above) but needs mypharmacy CSS tokens
// until design delivers the real c4u token palette.

export const storeName = process.env.NEXT_PUBLIC_STORE_NAME || 'mypharmacy';

export const brand: BrandConfig = brands[storeName] ?? brands.mypharmacy;

export type { BrandConfig };
